import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';
import Footer from './components/Footer';

import HomePage            from './pages/HomePage';
import SignUpPage          from './pages/SignUpPage';
import SignInPage          from './pages/SignInPage';
import MentorSearchPage    from './pages/MentorSearchPage';
import MentorProfilePage   from './pages/MentorProfilePage';
import SessionRequestPage  from './pages/SessionRequestPage';
import DashboardPage       from './pages/DashboardPage';
import MentorDashboardPage from './pages/MentorDashboardPage';
import PaymentPage         from './pages/PaymentPage';
import PaymentSuccessPage  from './pages/PaymentSuccessPage';
import PaymentCancelledPage from './pages/PaymentCancelledPage';
import ProfilePage         from './pages/ProfilePage';
import TermsPage           from './pages/TermsPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage  from './pages/ResetPasswordPage';

import './App.css';

function ProtectedRoute({ children, requireRole }) {
  const { user, profile, loading } = useAuth();

  // Still resolving auth state — show spinner
  if (loading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner" />
      </div>
    );
  }

  // Not logged in at all
  if (!user) return <Navigate to="/signin" replace />;

  // Role check — ONLY redirect when the profile is actually loaded.
  // If profile is null (fetch failed / still pending), do NOT redirect.
  // Redirecting with profile=null creates an infinite redirect loop:
  //   mentor → /dashboard (wrong role) → /mentor-dashboard → /dashboard → …
  if (requireRole && profile !== null && !profile?.is_admin && profile?.role !== requireRole) {
    if (requireRole === 'parent') return <Navigate to="/mentor-dashboard" replace />;
    if (requireRole === 'mentor') return <Navigate to="/dashboard"        replace />;
  }

  // If profile failed to load entirely show a recoverable error rather than a loop
  if (requireRole && profile === null) {
    return (
      <div className="page-container" style={{ textAlign: 'center', paddingTop: '4rem' }}>
        <p style={{ color: '#666', fontFamily: 'Open Sans, sans-serif', marginBottom: '1rem' }}>
          We couldn't load your profile. Please check your connection and try again.
        </p>
        <button
          className="btn btn-primary"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  return children;
}

function AdminRoute({ children }) {
  const { user, profile, loading } = useAuth();
  if (loading) return <div className="app-loading"><div className="app-loading-spinner" /></div>;
  if (!user) return <Navigate to="/signin" replace />;
  if (!profile?.is_admin) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { authError, clearAuthError } = useAuth();

  return (
    <div className="app-layout">
      <ScrollToTop />
      <Navbar />

      {authError && (
        <div
          style={{
            background: '#FDECEA',
            color: '#B71C1C',
            padding: '0.6rem 1.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '0.875rem',
            fontFamily: 'Open Sans, sans-serif',
            borderBottom: '1px solid #F5C6CB',
          }}
        >
          <span>
            <strong>Auth error:</strong> {authError}
          </span>
          <button
            onClick={clearAuthError}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.1rem',
              color: '#B71C1C',
              lineHeight: 1,
            }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <main className="app-main">
        <ErrorBoundary>
          <Routes>

            {/* Public Routes */}
            <Route path="/"                element={<HomePage />} />
            <Route path="/signup"          element={<SignUpPage />} />
            <Route path="/signin"          element={<SignInPage />} />
            <Route path="/mentors"         element={<MentorSearchPage />} />
            <Route path="/mentors/:mentorId" element={<MentorProfilePage />} />
            <Route path="/terms"           element={<TermsPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password"  element={<ResetPasswordPage />} />

            {/* Payment callbacks are public (Stripe redirects here) */}
            <Route path="/payment-success"   element={<PaymentSuccessPage />} />
            <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />

            {/* Parent-only */}
            <Route
              path="/request-session/:mentorId"
              element={
                <ProtectedRoute requireRole="parent">
                  <SessionRequestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requireRole="parent">
                  <DashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Mentor-only */}
            <Route
              path="/mentor-dashboard"
              element={
                <ProtectedRoute requireRole="mentor">
                  <MentorDashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Payment (Parent-only) */}
            <Route
              path="/payment/:sessionRequestId"
              element={
                <ProtectedRoute requireRole="parent">
                  <PaymentPage />
                </ProtectedRoute>
              }
            />

            {/* Any logged-in user */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminDashboardPage />
                </AdminRoute>
              }
            />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />

          </Routes>
        </ErrorBoundary>
      </main>

      <Footer />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
